// src/pages/inventory/ordering/drawers/EditPurchaseOrderDrawer.tsx
import React, { useState, useEffect } from "react";
import { extractValidationErrors } from "../../../../../utils/apiError";
import { Button, Form, Input, InputNumber, DatePicker, Select, Typography, Modal, Row, Col } from "antd";
import type { ColumnsType } from "antd/es/table";
import { SaveOutlined, DeleteOutlined, PlusOutlined, MinusCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import type { PurchaseOrder, PurchaseOrderItem } from "../../../../../types/inventory/OrderTypes";
import { createPurchaseOrder, updatePurchaseOrder, deletePurchaseOrder } from "../../../../../api/inventory/purchaseOrderApi";
import { fetchSuppliers, Supplier } from "../../../../../api/inventory/supplierApi";
import { fetchProducts } from "../../../../../api/inventory/productApi";
import { useNotifications } from "../../../../../context/NotificationContext";
import type { Product } from "../../../../../types/inventory/ProductTypes";
import { Company } from "../../../../../types/admin/CompanyTypes";
import { fetchClinicCompany } from "../../../../../api/admin/companyApi";
import { DetailDrawer, SectionCard, DataTable } from "../../../../../components/shared";

const { Title, Text } = Typography;
const { TextArea } = Input;

interface EditPurchaseOrderDrawerProps {
	isOpen: boolean;
	onClose: () => void;
	order: PurchaseOrder;
}

const EditPurchaseOrderDrawer: React.FC<EditPurchaseOrderDrawerProps> = ({ isOpen, onClose, order }) => {
	const [updatedOrder, setUpdatedOrder] = useState<PurchaseOrder>({ ...order });
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [errorMessages, setErrorMessages] = useState<string[]>([]);
	const [suppliers, setSuppliers] = useState<Supplier[]>([]);
	const [products, setProducts] = useState<Product[]>([]);
	const [items, setItems] = useState<PurchaseOrderItem[]>(order.items || []);
	const { showError, showSuccess } = useNotifications();
	const [company, setCompany] = useState<Company | null>(null);

	useEffect(() => {
		const loadCompany = async () => {
			let company: Company = await fetchClinicCompany();
			setCompany(company);
		};
		loadCompany();
	}, []);

	useEffect(() => {
		setUpdatedOrder({ ...order });
		setItems(order.items || []);
		loadSuppliers();
		loadProducts();
	}, [company, order]);

	useEffect(() => {
		calculateTotal();
	}, [items]);

	const loadSuppliers = async () => {
		try {
			const { data } = await fetchSuppliers(1, 100);
			setSuppliers(data);
		} catch (error) {
			console.error("Error loading suppliers:", error);
		}
	};

	const loadProducts = async () => {
		try {
			const { data } = await fetchProducts(1, 100, company.id, "", 0);
			setProducts(data);
		} catch (error) {
			console.error("Error loading products:", error);
		}
	};

	const calculateTotal = () => {
		const total = items.reduce((sum, item) => sum + item.quantity * item.unit_cost, 0);
		setUpdatedOrder((prev) => ({ ...prev, total_amount: total }));
	};

	const addItem = () => {
		setItems([...items, { product_id: 0, quantity: 1, unit_cost: 0, notes: "" }]);
	};

	const removeItem = (index: number) => {
		setItems(items.filter((_, i) => i !== index));
	};

	const updateItem = (index: number, field: string, value: any) => {
		const newItems = [...items];
		newItems[index] = { ...newItems[index], [field]: value };

		// Auto-fill unit cost from product
		if (field === "product_id") {
			const product = products.find((p) => p.product_id === value);
			if (product && product.unit_cost) {
				newItems[index].unit_cost = product.unit_cost;
			}
		}

		setItems(newItems);
	};

	const saveOrder = async () => {
		setIsSubmitting(true);
		setErrorMessages([]);
		try {
			if (!updatedOrder.supplier_id || items.length === 0) {
				setErrorMessages(["Supplier and at least one item are required."]);
				setIsSubmitting(false);
				return;
			}

			const orderData = { ...updatedOrder, items };

			if (order.purchase_order_id) {
				const response = await updatePurchaseOrder(order.purchase_order_id, orderData);
				setUpdatedOrder(response);
				showSuccess("Purchase order updated successfully");
			} else {
				const newOrder = await createPurchaseOrder(orderData);
				setUpdatedOrder(newOrder);
				showSuccess("Purchase order created successfully");
			}
		} catch (error: unknown) {
			handleErrors(error);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleDelete = () => {
		Modal.confirm({
			title: "Delete Purchase Order",
			content: "Are you sure you want to delete this purchase order? This action cannot be undone.",
			okText: "Delete",
			okType: "danger",
			cancelText: "Cancel",
			onOk: async () => {
				try {
					if (order.purchase_order_id) {
						await deletePurchaseOrder(order.purchase_order_id);
						showSuccess("Purchase order deleted successfully");
						onClose();
					}
				} catch (error) {
					handleErrors(error);
				}
			},
		});
	};

	const handleErrors = (error: unknown) => {
		setErrorMessages(extractValidationErrors(error));
		showError("Failed to save purchase order");
	};

	const itemColumns: ColumnsType<PurchaseOrderItem & { index: number }> = [
		{
			title: "Product",
			dataIndex: "product_id",
			key: "product_id",
			render: (value: number, record, index: number) => (
				<Select style={{ width: "100%" }} value={value || undefined} onChange={(val) => updateItem(index, "product_id", val)} showSearch optionFilterProp="children">
					{products.map((product) => (
						<Select.Option key={product.product_id} value={product.product_id}>
							{product.name} ({product.sku})
						</Select.Option>
					))}
				</Select>
			),
		},
		{
			title: "Quantity",
			dataIndex: "quantity",
			key: "quantity",
			width: 120,
			render: (value: number, record, index: number) => <InputNumber min={1} value={value} onChange={(val) => updateItem(index, "quantity", val || 1)} style={{ width: "100%" }} />,
		},
		{
			title: "Unit Cost",
			dataIndex: "unit_cost",
			key: "unit_cost",
			width: 130,
			render: (value: number, record, index: number) => <InputNumber prefix="$" precision={2} min={0} value={value} onChange={(val) => updateItem(index, "unit_cost", val || 0)} style={{ width: "100%" }} />,
		},
		{
			title: "Total",
			key: "total",
			width: 100,
			align: "right",
			render: (_, record) => <Text strong>${(record.quantity * record.unit_cost).toFixed(2)}</Text>,
		},
		{
			title: "",
			key: "actions",
			width: 50,
			render: (_, record, index: number) => (
				<Button type="text" danger size="small" icon={<MinusCircleOutlined />} onClick={(e) => { e.stopPropagation(); removeItem(index); }} />
			),
		},
	];

	return (
		<DetailDrawer
			open={isOpen}
			onClose={onClose}
			width="85vw"
			title={order.purchase_order_id ? `Edit Purchase Order PO-${order.purchase_order_id}` : "Create New Purchase Order"}
			headerActions={
				<>
					<Button type="primary" icon={<SaveOutlined />} onClick={saveOrder} loading={isSubmitting}>
						Save
					</Button>
					{order.purchase_order_id && (
						<Button danger icon={<DeleteOutlined />} onClick={handleDelete} disabled={isSubmitting}>
							Delete
						</Button>
					)}
				</>
			}
			errorMessages={errorMessages}
			onClearErrors={() => setErrorMessages([])}
		>
			<SectionCard title="Order Details">
				<Form layout="vertical">
					<Row gutter={[16, 0]}>
						<Col xs={24} md={12}>
							<Form.Item label="Supplier" required>
								<Select value={updatedOrder.supplier_id || undefined} onChange={(value) => setUpdatedOrder((prev) => ({ ...prev, supplier_id: value }))} showSearch optionFilterProp="children">
									{suppliers.map((supplier) => (
										<Select.Option key={supplier.supplier_id} value={supplier.supplier_id}>
											{supplier.name}
										</Select.Option>
									))}
								</Select>
							</Form.Item>
						</Col>
						<Col xs={24} md={12}>
							<Form.Item label="Order Date">
								<DatePicker
									style={{ width: "100%" }}
									value={updatedOrder.order_date ? dayjs(updatedOrder.order_date) : null}
									onChange={(date) =>
										setUpdatedOrder((prev) => ({
											...prev,
											order_date: date ? date.toISOString() : "",
										}))
									}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={12}>
							<Form.Item label="Status">
								<Select value={updatedOrder.status} onChange={(value) => setUpdatedOrder((prev) => ({ ...prev, status: value }))}>
									<Select.Option value="pending">Pending</Select.Option>
									<Select.Option value="processing">Processing</Select.Option>
									<Select.Option value="completed">Completed</Select.Option>
									<Select.Option value="cancelled">Cancelled</Select.Option>
								</Select>
							</Form.Item>
						</Col>
						<Col xs={24}>
							<Form.Item label="Notes">
								<TextArea rows={2} value={updatedOrder.notes || ""} onChange={(e) => setUpdatedOrder((prev) => ({ ...prev, notes: e.target.value }))} placeholder="Order notes..." />
							</Form.Item>
						</Col>
					</Row>
				</Form>
			</SectionCard>

			<SectionCard
				title="Order Items"
				noPadding
				extra={
					<Button type="dashed" icon={<PlusOutlined />} onClick={addItem}>
						Add Item
					</Button>
				}
			>
				<DataTable
					bare
					columns={itemColumns}
					dataSource={items.map((item, index) => ({ ...item, index }))}
					rowKey="index"
					pagination={false}
					emptyTitle="No items added"
					emptyDescription="Click 'Add Item' to add products to this order"
				/>
				<div style={{ display: "flex", justifyContent: "flex-end", padding: "16px 20px", borderTop: `1px solid var(--mg-border)` }}>
					<Text strong style={{ fontSize: 18 }}>
						Total: ${updatedOrder.total_amount?.toFixed(2) || "0.00"}
					</Text>
				</div>
			</SectionCard>
		</DetailDrawer>
	);
};

export default EditPurchaseOrderDrawer;
